import axios, { AxiosRequestHeaders } from "axios";
import Cookies from "js-cookie";
import md5 from "md5";
import { toast } from "sonner";

/**
 * 生成签名
 */
function getSign({
  params,
  timestamp,
}: {
  params?: any;
  timestamp: number;
}) {
  const secret = Cookies.get("%24RM_signature");
  const sessionId = Cookies.get("session-id");
  const dSessionId = Cookies.get("d-session-id");
  const sSessionId = Cookies.get("s-session-id");
  const mSessionId = Cookies.get("m-session-id");

  let sign = secret ?? "";
  sign += `appkey${"8808181"}`;

  if (params) {
    sign += `data${JSON.stringify(params)}`;
  }
  if (dSessionId) sign += `d-session-id${dSessionId}`;
  if (mSessionId) sign += `m-session-id${mSessionId}`;
  if (sSessionId) sign += `s-session-id${sSessionId}`;
  if (sessionId) sign += `session-id${sessionId}`;

  let timeDifference = 0;
  sign += `timestamp${timestamp + timeDifference}`;

  return md5(sign).toUpperCase();
}

const MAX_RETRY_COUNT = 3;

/** 公开币种接口：失败时不触发未登录跳转（也不弹业务错误 toast） */
function isPublicCurrencyQuery(config: { url?: string } | undefined) {
  return String(config?.url ?? "").includes("/ecommerce/queryAllCurrency");
}

// 创建 axios 实例
const request = axios.create({
  timeout: 10 * 1000,
  baseURL: "/apigateway",
  method: "POST",
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    "Content-Type": "application/x-www-form-urlencoded",
  },
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // @ts-ignore
    const originData = config.originData ?? config.data ?? {};
    console.warn("[Axios Request]", config.url, originData);
    const data = originData;
    const timestamp = Date.now();
    const sign = getSign({
      params: data,
      timestamp,
    });
    const urlencoded = new URLSearchParams();

    // 文件上传
    if (data instanceof FormData) {
      return config;
    }

    // 分页兼容
    if (data?.current) {
      data.pageNo = data.current;
      delete data.current;
    }

    urlencoded.append("appkey", "8808183");
    urlencoded.append("timestamp", timestamp.toString());
    urlencoded.append("sign", sign);
    urlencoded.append("data", JSON.stringify(data));

    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    const locale = pathname.startsWith("/zh") ? "zh" : "en";
    const acceptLanguage = locale === "en" ? "en" : "zh-CN";

    return {
      ...config,
      data: urlencoded,
      originData: config.data,
      headers: {
        ...config.headers,
        "Accept-Language": acceptLanguage,
      } as unknown as AxiosRequestHeaders,
    };
  },
  (error) => {
    console.error("[Axios Request Error]", error);
    return Promise.reject(error);
  },
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const { data } = response;
    const { code, msg } = data;
    console.warn("[Axios Response]", response.config.url, data);

    if (code === -9580106) {
      // 数据签名异常：刷新 secret 重试
      Cookies.set("%24RM_signature", data.data.secret);
      // @ts-ignore
      const retryCount = response.config?.retryCount || 0;
      if (retryCount < MAX_RETRY_COUNT) {
        return request(response.config.url as string, {
          ...response.config,
          // @ts-ignore
          data: response.config.originData,
          // @ts-ignore
          retryCount: retryCount + 1,
        });
      }
    }

    if (code !== 0) {
      const publicCurrency = isPublicCurrencyQuery(response.config);
      const suppressToast =
        publicCurrency && (code === -9580108 || code === -200);

      if (
        typeof window !== "undefined" &&
        !response.config.origin &&
        !suppressToast
      ) {
        toast.error(`${data.msg}`);
      }

      if (
        code === -9580108 &&
        typeof window !== "undefined" &&
        !publicCurrency
      ) {
        const pathname = window.location.pathname;
        const locale = pathname.startsWith("/zh") ? "zh" : "en";
        const isAccountPage = pathname.includes("/account");
        if (!isAccountPage) {
          window.location.href = `/${locale}/account/login`;
        }
      }

      const noAuthCodes = [200];
      if (noAuthCodes.includes(code)) {
        // @ts-ignore
        return response.config?.origin ? data : null;
      }

      if (code !== 200 && typeof window === "undefined") {
        throw new Error(response.data?.msg ?? "");
      } else if (
        typeof window !== "undefined" &&
        code === -200 &&
        !isPublicCurrencyQuery(response.config)
      ) {
        const pathname = window.location.pathname;
        const locale = pathname.startsWith("/zh") ? "zh" : "en";
        const isAccountPage = pathname.includes("/account");
        if (!isAccountPage) {
          window.location.href = `/${locale}/account/login`;
        }
      }
    }

    // @ts-ignore
    return response.config?.origin ? data : data.data;
  },
  (error) => {
    console.error("[Axios Response Error]", error);
    return Promise.reject(error);
  },
);

export default request;
